import React, { useState, useMemo } from 'react';
import type { ReactElement } from 'react';
import accounting from 'accounting';
import { sumBy, map, filter } from 'lodash';
import cIf from 'classnames';
import TranslatorManager from 'ts-translate-js';
import type { PriceGroupModel } from '../../../mvcapi/models/buyticket';
import type { TicketSelectionWarningReport } from '../../../logic/models/ticketselectionwarning';

import './price-list-quantity-selector.scss';
import { getWarningsListWithSpecificFilters, isPriceGroupCreditCardConditionSeen } from '../../../logic/buytickets';
import { TicketSelectionWarningList } from './ticket-selection-warning-list';
import { useIFrameResize } from '../../../logic/hooks/utils';
import { useAddTicketsToCartStore } from '../../../stores';
import { TicketQtySelector } from '../../organisms/selecttickets/ticket-qty-selector';
import { PriceQuantity } from '../../../mvcapi/models/buyticket/ticketselection';

const tr = TranslatorManager.boundTR;

type PriceListQuantitySelectorProps = {
    quantityToSelect: number;
    priceGroups: PriceGroupModel[];
    ticketSelectionWarningReport: TicketSelectionWarningReport;
    activeCreditCardConditions: any[];
    onCancel: () => void;
    onSelection: (pricesQty: PriceQuantity[]) => void;
    onRequiredCreditCardCondition: (pg: PriceGroupModel) => Promise<boolean>;
    sectionSpecialMessage?: string;
    taxesOnlyAtCheckout?: boolean;
}



type InsufficiantQtyData = {
    show: boolean;
    qty?: number;
    message?: string;
}

// INFO: New sale

export function PriceListQuantitySelector({quantityToSelect, priceGroups, ticketSelectionWarningReport, activeCreditCardConditions, onCancel, onSelection, onRequiredCreditCardCondition, sectionSpecialMessage, taxesOnlyAtCheckout}: PriceListQuantitySelectorProps): ReactElement {
    const [ creditCardConditionsSeen, setCreditCardConditionAsSeen ] = useAddTicketsToCartStore(s => [ s.creditCardConditionsSeen, s.setCreditCardConditionAsSeen ]);
    const [pricesQty, setPricesQty] = useState<PriceQuantity[]>([]);
    const eventDate = useAddTicketsToCartStore(s => s.eventDate);

    useIFrameResize();

    const handlePriceQty = async (priceGroup: PriceGroupModel, qty: number) => {
        if (priceGroup.RequiresCreditCardCondition && !isPriceGroupCreditCardConditionSeen(priceGroup.RequiredCreditCardConditionID, activeCreditCardConditions, creditCardConditionsSeen)) {
            const accept = await onRequiredCreditCardCondition(priceGroup);
            if (!accept) {
                return;
            }
            setCreditCardConditionAsSeen(priceGroup.RequiredCreditCardConditionID);
        } 
        
        const existingPriceGroup = pricesQty.find(p => p.priceGroup.PriceID === priceGroup.PriceID);
        let newPricesQty = [...pricesQty];
        if (existingPriceGroup == undefined) {
            newPricesQty.push({
                priceGroup: priceGroup,
                quantity: qty
            });
        } else {
            const index = newPricesQty.indexOf(existingPriceGroup);
            existingPriceGroup.quantity = qty;
            newPricesQty[index] = existingPriceGroup;
        }

        setPricesQty(newPricesQty);
    };

    const insufficiantQtyData: InsufficiantQtyData = useMemo(() => {
        const qtyTotal = sumBy(pricesQty, 'quantity');
        if (qtyTotal !== quantityToSelect) {
            const data: InsufficiantQtyData = {
                show: true,
                qty: quantityToSelect - qtyTotal
            };

            data.message = data.qty > 0 ? tr("Billets non distribués : {qty}", {qty: data.qty}) : tr("Billets en trop : {qty}", {qty: data.qty * -1});
            return data;
        }
        return {show: false};
    }, [pricesQty, quantityToSelect]);

    return <div className="price-list-quantity-selector">
        {
            map(filter(priceGroups, ["Hidden", false]), (pg: PriceGroupModel) => {
                const warnings = ticketSelectionWarningReport.priceListWarnings.find(wl => wl.priceId === pg.PriceID)?.warnings ?? [];
                const filteredWarnings = getWarningsListWithSpecificFilters(warnings);
                const isDisabledByWarning = filteredWarnings.find(w => w.disabled) != undefined;
                const isDisabled = pg.Disabled || isDisabledByWarning;

                return <div key={pg.PriceID} className={cIf(["price-quantity-selector", {"disabled": isDisabled}])}>
                    <div className="info">
                        <span>{pg.Name}</span>
                        <span className="price accented-text">{accounting.formatMoney(taxesOnlyAtCheckout ? pg.TotalBeforeTaxes : pg.Total)}</span>
                    </div>
                    <TicketSelectionWarningList warnings={filteredWarnings} />
                    {
                        !isDisabled && <div className='ticket-qty-selector right'>
                            <TicketQtySelector
                                maxQty={10}
                                limitQtySales={eventDate.LimitQtySales}
                                suggestedQtySales={eventDate.SuggestedQtySales}
                                internetTicketQTYMultipleOf={eventDate.InternetTicketQTYMultipleOf}
                                defaultValue={0}
                                onQtyChange={qty => handlePriceQty(pg, qty)}
                            />
                        </div>
                    }
                </div>;
            }
        )}
        {insufficiantQtyData.show && <span className="invalid-qty kind-error">{insufficiantQtyData.message}</span>}
        {
            sectionSpecialMessage && <div className="specialMessage">
                <div className="alert alert-warning" role="alert">
                    <strong>{tr(sectionSpecialMessage)}</strong>
                </div>
            </div>
        }
        <div className="actions">
            <button onClick={onCancel} className="btn btn-default">{tr("Annuler")}</button>
            <button onClick={() => onSelection(pricesQty)} className="btn btn-primary" disabled={insufficiantQtyData.show}>{tr("Ajouter")}</button>
        </div>
    </div>;
}